
import React from "react";
import { Reagent } from "@/types/reagent.types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/utils/dateUtils";
import LoadingSpinner from "@/components/common/LoadingSpinner";

interface UserReagentsSectionProps {
  reagents: Reagent[];
  isLoading: boolean;
}

const UserReagentsSection: React.FC<UserReagentsSectionProps> = ({ 
  reagents,
  isLoading
}) => {
  const navigate = useNavigate();
  
  const viewReagent = (id: string) => {
    navigate(`/reagents/${id}`);
  };
  
  if (isLoading) {
    return (
      <Card className="mt-6">
        <CardHeader className="pb-4">
          <CardTitle>Available Reagents</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center py-12">
          <LoadingSpinner size="md" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-6">
      <CardHeader className="pb-4">
        <CardTitle>Available Reagents</CardTitle>
      </CardHeader>
      <CardContent>
        {reagents.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">This user hasn't uploaded any reagents yet.</p>
          </div>
        ) : (
          <div className="divide-y">
            {reagents.map((reagent) => (
              <div key={reagent.id} className="py-4 first:pt-0 last:pb-0">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium">{reagent.name}</h3>
                    {(reagent.category || (reagent.categories && reagent.categories.name)) && (
                      <p className="text-sm text-muted-foreground">
                        {reagent.category || (reagent.categories && reagent.categories.name) || "Uncategorized"}
                      </p>
                    )}
                    
                    <div className="flex flex-wrap gap-x-4 mt-1 text-sm">
                      {reagent.amount && (
                        <span>
                          {reagent.amount} 
                          {reagent.amount_unit || ""}
                        </span>
                      )}
                      
                      {(reagent.expiryDate || reagent.expiry_date) && (
                        <span>Expires: {formatDate(reagent.expiryDate || reagent.expiry_date)}</span>
                      )}
                    </div>
                    
                    {reagent.description && (
                      <p className="mt-2 text-sm line-clamp-2">{reagent.description}</p>
                    )}
                  </div>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => viewReagent(reagent.id)}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserReagentsSection;
